import { ApiErrorData, ApiResponse } from 'models/ApiResponse';
import {
  CreateDashboardVideoInput,
  DashboardVideo,
  DashboardVideoDetail,
  DashboardVideosListParams,
  DashboardVideosListResponse,
  DeleteDashboardVideoResponse,
  ThumbnailUploadUrlInput,
  ThumbnailUploadUrlResponse,
  UpdateDashboardVideoInput,
  UpdateDashboardVideoStatusInput,
  UpdateDashboardVideoStatusResponse,
  UpdateVideoOfMonthInput,
  UpdateVideoOfMonthResponse,
  VideoUploadUrlInput,
  VideoUploadUrlResponse,
} from 'models/DashboardVideo';

import { FunctionsHelper } from 'utils/FunctionsHelper';
import MainBackendAxios from 'utils/MainBackendAxios';

const BASE = '/dashboard-videos';

export const DashboardVideoService = {
  /**
   * Paso 1 del flujo de upload (3 pasos): obtiene una presigned PUT URL para
   * subir el video .mp4 directamente a R2. El PUT (Paso 2) va directo al bucket
   * con `Content-Type: video/mp4` y NO debe pasar por MainBackendAxios. El Paso 3
   * persiste el video con el `r2Key` recibido. Ver dashboard-videos-api.md §5.
   */
  getVideoUploadUrl: async (
    body: VideoUploadUrlInput
  ): Promise<ApiResponse<VideoUploadUrlResponse | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.post(
        `${BASE}/video-upload-url`,
        body
      );
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  /**
   * Paso 1 del flujo de upload de thumbnail: obtiene una presigned PUT URL para
   * subir la imagen directamente a R2. El PUT (Paso 2) usa el `contentType`
   * devuelto EXACTO y NO debe pasar por MainBackendAxios. Ver dashboard-videos-api.md §5.
   */
  getThumbnailUploadUrl: async (
    body: ThumbnailUploadUrlInput
  ): Promise<ApiResponse<ThumbnailUploadUrlResponse | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.post(
        `${BASE}/thumbnail-upload-url`,
        body
      );
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  createDashboardVideo: async (
    body: CreateDashboardVideoInput
  ): Promise<ApiResponse<DashboardVideo | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.post(`${BASE}`, body);
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  getDashboardVideos: async (
    params: DashboardVideosListParams
  ): Promise<ApiResponse<DashboardVideosListResponse | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.get(`${BASE}`, { params });
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  getDashboardVideoById: async (
    id: string
  ): Promise<ApiResponse<DashboardVideoDetail | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.get(`${BASE}/${id}`);
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  updateDashboardVideo: async (
    id: string,
    body: UpdateDashboardVideoInput
  ): Promise<ApiResponse<DashboardVideo | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.patch(`${BASE}/${id}`, body);
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  deleteDashboardVideo: async (
    id: string
  ): Promise<ApiResponse<DeleteDashboardVideoResponse | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.delete(`${BASE}/${id}`);
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  updateDashboardVideoStatus: async (
    id: string,
    body: UpdateDashboardVideoStatusInput
  ): Promise<
    ApiResponse<UpdateDashboardVideoStatusResponse | ApiErrorData>
  > => {
    try {
      const response = await MainBackendAxios.patch(
        `${BASE}/${id}/status`,
        body
      );
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  updateVideoOfMonth: async (
    id: string,
    body: UpdateVideoOfMonthInput
  ): Promise<ApiResponse<UpdateVideoOfMonthResponse | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.patch(
        `${BASE}/${id}/video-of-month`,
        body
      );
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },
};
